
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, ExternalLink, Building2, Target, Brain } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { generateRegistrationLink, generateWorkBehaviorLink, generateCognitiveAssessmentLink, copyToClipboard } from '@/utils/registrationLinks';
import { findOrCreateCompany } from '@/utils/companyUtils';

interface CompanyData {
  slug: string;
}

const CompanyRegistrationTab = () => {
  const { user } = useAuth();
  const { companyInfo, loading: companyInfoLoading, error: companyInfoError } = useCompanyInfo();
  const { toast } = useToast();
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [workBehaviorCopied, setWorkBehaviorCopied] = useState(false);
  const [cognitiveAssessmentCopied, setCognitiveAssessmentCopied] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);

  useEffect(() => {
    const fetchOrCreateCompanyData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // If user has no company, auto-create one
      if (!companyInfoLoading && !companyInfo?.id) {
        console.log('CompanyRegistrationTab: User has no company, auto-creating...');
        setAutoCreating(true);
        
        try {
          // Get user profile for company name
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', user.id)
            .single();

          const companyName = profile?.full_name || profile?.email?.split('@')[0] || 'My Company';
          
          // Auto-create company
          const company = await findOrCreateCompany({
            companyName: companyName,
            companySize: 'small'
          });

          // Associate user with company
          const { error: associateError } = await supabase.rpc('associate_user_with_company', {
            p_user_id: user.id,
            p_company_id: company.id
          });

          if (associateError) throw associateError;

          toast({
            title: "Company Created!",
            description: `${companyName} has been created automatically. Your registration link is now ready.`
          });

          // Notify CompanyContext to refetch (no hard reload needed)
          window.dispatchEvent(new CustomEvent('company-created'));
          return;
        } catch (error: any) {
          console.error('Error auto-creating company:', error);
          toast({
            title: "Error",
            description: "Failed to create your company automatically. Please refresh the page.",
            variant: "destructive"
          });
        } finally {
          setAutoCreating(false);
        }
      }

      // If user has a company, fetch company data
      if (companyInfo?.id) {
        try {
          const { data: company, error } = await supabase
            .from('companies')
            .select('slug')
            .eq('id', companyInfo.id)
            .single();

          if (error) throw error;
          setCompanyData(company);
        } catch (error) {
          console.error('Error fetching company data:', error);
          toast({
            title: "Error",
            description: "Failed to load company registration settings",
            variant: "destructive"
          });
        }
      }
      
      setLoading(false);
    };

    if (!companyInfoLoading) {
      fetchOrCreateCompanyData();
    }
  }, [companyInfo, companyInfoLoading, user, toast]);

  const registrationLink = companyData?.slug ? generateRegistrationLink(companyData.slug) : '';
  const workBehaviorLink = companyData?.slug ? generateWorkBehaviorLink(companyData.slug) : '';
  const cognitiveAssessmentLink = companyData?.slug ? generateCognitiveAssessmentLink(companyData.slug) : '';

  const handleCopyLink = async () => {
    if (!registrationLink) return;
    const success = await copyToClipboard(registrationLink);
    if (success) {
      setCopied(true);
      toast({
        title: "Link Copied!",
        description: `Registration link for ${companyInfo?.name} copied to clipboard`
      });
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast({
        title: "Copy Failed",
        description: "Unable to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleCopyWorkBehaviorLink = async () => {
    if (!workBehaviorLink) return;
    const success = await copyToClipboard(workBehaviorLink);
    if (success) {
      setWorkBehaviorCopied(true);
      toast({
        title: "Link Copied!",
        description: `Work Behavior Assessment link for ${companyInfo?.name} copied to clipboard`
      });
      setTimeout(() => setWorkBehaviorCopied(false), 2000);
    } else {
      toast({
        title: "Copy Failed",
        description: "Unable to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleOpenLink = () => {
    if (registrationLink) {
      window.open(registrationLink, '_blank');
    }
  };

  const handleOpenWorkBehaviorLink = () => {
    if (workBehaviorLink) {
      window.open(workBehaviorLink, '_blank');
    }
  };

  const handleCopyCognitiveAssessmentLink = async () => {
    if (!cognitiveAssessmentLink) return;
    const success = await copyToClipboard(cognitiveAssessmentLink);
    if (success) {
      setCognitiveAssessmentCopied(true);
      toast({
        title: "Link Copied!",
        description: `Cognitive Assessment link for ${companyInfo?.name} copied to clipboard`
      });
      setTimeout(() => setCognitiveAssessmentCopied(false), 2000);
    } else {
      toast({
        title: "Copy Failed",
        description: "Unable to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleOpenCognitiveAssessmentLink = () => {
    if (cognitiveAssessmentLink) {
      window.open(cognitiveAssessmentLink, '_blank');
    }
  };

  if (loading || companyInfoLoading || autoCreating) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-foreground mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {autoCreating ? 'Creating your company...' : 'Loading registration settings...'}
          </p>
        </div>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Company Settings Unavailable</h3>
        <p className="text-muted-foreground">Unable to load company registration settings.</p>
        {companyInfoError && <p className="text-destructive text-sm mt-2">Error: {companyInfoError}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Registration Link Card */}
      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-card-foreground">
            <Building2 className="h-5 w-5" />
            Company Registration Link
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Share this link with new employees to join {companyInfo?.name}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          
          <div className="space-y-2">
            <Label htmlFor="registration-link">Registration Link</Label>
            <div className="flex gap-2">
              <Input 
                id="registration-link" 
                value={registrationLink} 
                readOnly 
                className="bg-muted font-mono text-sm" 
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                size="sm"
                disabled={copied}
                className="shrink-0"
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button onClick={handleOpenLink} variant="outline" size="sm" className="shrink-0">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Work Behavior Assessment Link Card */}
      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-card-foreground">
            <Target className="h-5 w-5" />
            Work Behavior Assessment Link
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Share this link for standalone work behavior assessments. Candidates will only see a thank you page after completion.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          
          <div className="space-y-2">
            <Label htmlFor="work-behavior-link">Work Behavior Assessment Link</Label>
            <div className="flex gap-2">
              <Input 
                id="work-behavior-link" 
                value={workBehaviorLink} 
                readOnly 
                className="bg-muted font-mono text-sm" 
              />
              <Button
                onClick={handleCopyWorkBehaviorLink}
                variant="outline"
                size="sm"
                disabled={workBehaviorCopied}
                className="shrink-0"
              >
                {workBehaviorCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button onClick={handleOpenWorkBehaviorLink} variant="outline" size="sm" className="shrink-0">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cognitive Assessment Link Card */}
      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-card-foreground">
            <Brain className="h-5 w-5" />
            Cognitive Assessment Link
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Share this link for cognitive assessments. Candidates must register before taking the test and will see a thank you page after completion.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          
          <div className="space-y-2">
            <Label htmlFor="cognitive-assessment-link">Cognitive Assessment Link</Label>
            <div className="flex gap-2">
              <Input 
                id="cognitive-assessment-link" 
                value={cognitiveAssessmentLink} 
                readOnly 
                className="bg-muted font-mono text-sm" 
              />
              <Button
                onClick={handleCopyCognitiveAssessmentLink}
                variant="outline"
                size="sm"
                disabled={cognitiveAssessmentCopied}
                className="shrink-0"
              >
                {cognitiveAssessmentCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button onClick={handleOpenCognitiveAssessmentLink} variant="outline" size="sm" className="shrink-0">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Information Card */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <h4 className="font-semibold text-primary mb-2">How to use your assessment links:</h4>
        <ul className="text-sm text-primary/80 space-y-1">
          <li>• <strong>Registration Link:</strong> Full personality assessment + company registration</li>
          <li>• <strong>Work Behavior Link:</strong> Standalone work behavior assessment only (candidates see thank you page)</li>
          <li>• <strong>Cognitive Assessment Link:</strong> Standalone cognitive assessment with required registration (candidates see thank you page)</li>
          <li>• All results are linked to your company and viewable in the admin panel</li>
          <li>• You can view behavioral profiles, cognitive scores, and work behavior assessments for comparison</li>
        </ul>
      </div>
    </div>
  );
};

export default CompanyRegistrationTab;
